import React, { useState } from 'react';
import styled from 'styled-components';
import { paperTextureSmall } from '../../paperTexture';
import { useDeviceStore } from '../../../store/deviceStore';
import { DeviceType } from '../../../enum/EnumRepository';
import { imHere, imHereSmall } from '../../../component/Animation';

const BUTTON_SIZES = {
  small: { icon: 10, padding: 4 },
  fit: { icon: 14, padding: 6 },
  standard: { icon: 14, padding: 8 },
} as const;

type IconBtnSize = keyof typeof BUTTON_SIZES;

interface UniversalIconBtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size: IconBtnSize;
  border: boolean;
  backgroundImg: string;
  backgroundImgHover?: string;
  isPrimary?: boolean;
  isTransparent?: boolean;
  /** true면 기본 0.2 → hover 시 1 */
  hoverOpacity?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const DIMMED_OPACITY = 0.2;

const UniversalIconBtn: React.FC<UniversalIconBtnProps> = ({
  isPrimary = false,
  isTransparent = false,
  size,
  border,
  backgroundImg,
  backgroundImgHover,
  hoverOpacity = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
  children,
  type = 'button',
  disabled,
  ...buttonProps
}) => {
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const [isHovered, setIsHovered] = useState(false);
  const opacity = hoverOpacity ? DIMMED_OPACITY : 1;
  const { icon, padding } = BUTTON_SIZES[size];

  return (
    <UniversalIconBtnWrapper
      {...buttonProps}
      type={type}
      disabled={disabled}
      $isPrimary={isPrimary}
      $isTransparent={isTransparent}
      $isMobile={isMobile}
      $iconSize={icon}
      $padding={padding}
      $hasChildren={children != null}
      $border={border}
      $opacity={opacity}
      onClick={onClick}
      onMouseEnter={(event) => {
        if (!disabled) setIsHovered(true);
        onMouseEnter?.(event);
      }}
      onMouseLeave={(event) => {
        setIsHovered(false);
        onMouseLeave?.(event);
      }}
    >
      <IconBtn
        aria-hidden="true"
        $iconSize={icon}
        $backgroundImg={isHovered && backgroundImgHover ? backgroundImgHover : backgroundImg}
      />
      {children}
    </UniversalIconBtnWrapper>
  );
};

const UniversalIconBtnWrapper = styled.button<{
  $isMobile: boolean;
  $isTransparent: boolean;
  $iconSize: number;
  $padding: number;
  $hasChildren: boolean;
  $border: boolean;
  $isPrimary: boolean;
  $opacity: number;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  width: ${({ $hasChildren, $iconSize, $padding }) => $hasChildren ? 'auto' : `${$iconSize + $padding * 2}px`};
  min-width: ${({ $iconSize, $padding }) => $iconSize + $padding * 2}px;
  height: ${({ $iconSize, $padding }) => $iconSize + $padding * 2}px;
  padding: ${({ $padding }) => $padding}px;
  gap: 4px;
  font: inherit;
  color: inherit;
  opacity: ${({ $opacity }) => $opacity};
  
  &:not(:disabled):hover {
    transform: scale(0.92);
    opacity: 1;
  }
  transition: all 0.35s ease-in-out;
  ${({ $isTransparent }) => ($isTransparent ? '' : paperTextureSmall)}
  border: none;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    box-sizing: border-box;
    border: ${({ $border, theme }) => ($border ? `0.5px solid ${theme.colors.color_Gray_06}` : 'none')};
    border-radius: inherit;
    pointer-events: none;
  }
  ${({ $isPrimary, $isMobile }) => {
    if (!$isPrimary) return '';
    return $isMobile ? imHereSmall : imHere;
  }}
  background-color: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.colors.color_Gray_03};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const IconBtn = styled.span<{ $iconSize: number, $backgroundImg: string }>`
  flex-shrink: 0;
  width: ${({ $iconSize }) => $iconSize}px;
  height: ${({ $iconSize }) => $iconSize}px;
  background-image: url(${({ $backgroundImg }) => $backgroundImg });
  background-size: cover;
  background-position: center;
  transition: background-image 0.3s ease-in-out;
`;

export default UniversalIconBtn;
